import React, { useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Image, Spin, Typography } from 'antd';
import { getDownloadURL, ref } from 'firebase/storage';
import { useIntl } from 'react-intl';

import { storage } from '@src/firebase/firebase';
import type { TimelineEventTypeComponent } from '@src/packages/events/components/TimelineEvent/TimelineEvent.types';
import IntlMessages from '@src/util/IntlMessages';

import styles from './index.module.less';

export type SignatureProps = {
  component: TimelineEventTypeComponent;
};

const Signature: React.FC<SignatureProps> = ({ component }) => {
  const intl = useIntl();
  const signaturePath = component.signatureValue?.url;
  const [imageUrl, setImageUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(Boolean(signaturePath));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    if (!signaturePath) {
      setImageUrl(undefined);
      setIsLoading(false);
      setHasError(false);
      return () => {
        isCurrent = false;
      };
    }

    const loadSignature = async () => {
      setImageUrl(undefined);
      setIsLoading(true);
      setHasError(false);

      try {
        const url = await getDownloadURL(ref(storage, signaturePath));
        if (isCurrent) {
          setImageUrl(url);
          setIsLoading(false);
        }
      } catch {
        if (isCurrent) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    void loadSignature();

    return () => {
      isCurrent = false;
    };
  }, [signaturePath]);

  const clarification = component.componentProps?.includeClarification
    ? component.clarificationValue?.trim()
    : undefined;

  return (
    <div className={styles.container}>
      {isLoading && (
        <Typography.Text className={styles.status} role="status" aria-live="polite">
          <Spin size="small" />
          <IntlMessages id="eventTypes.component.loadingSignature" />
        </Typography.Text>
      )}
      {!signaturePath && (
        <Typography.Text type="secondary">
          <IntlMessages id="eventTypes.component.notAdded" />
        </Typography.Text>
      )}
      {hasError && (
        <Typography.Text type="danger" role="alert" className={styles.status}>
          <ExclamationCircleOutlined aria-hidden="true" />
          <IntlMessages id="eventTypes.component.signatureNotFound" />
        </Typography.Text>
      )}
      {imageUrl && !isLoading && (
        <Image
          alt={component.label?.name || intl.formatMessage({ id: 'eventTypes.component.signature' })}
          className={styles.image}
          preview={{ getContainer: () => document.body }}
          src={imageUrl}
        />
      )}
      {clarification && (
        <div className={styles.clarification}>
          <span className={styles.clarificationLabel}>
            <IntlMessages id="eventTypes.component.clarification" />
          </span>
          <span>{clarification}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(Signature);
